/**
 * Shared types + pure normalization for the acquisition-touch ledger.
 *
 * Plan: "Marketing & Sales Effort Visibility Engine", sections P0.1
 * (`acquisition_touches` schema) and P0.3(d). This file carries ONLY the
 * type / static-table / pure-function slice — no D1, no schema import, no
 * `next/*` import. The Drizzle row type, the table itself, and the
 * `upsertTouch`/`promoteTouches` write path land in a later PR
 * (`src/lib/db/queries/acquisition-touches.ts`, `src/lib/db/schema.ts`) once
 * the schema migration ships; wiring this into `posthog-provider.tsx` /
 * the `/api/attribution/touch` beacon route is P0-b, not this PR.
 *
 * `FIRST_TOUCH_KEYS` is the single source of truth for the `ld_attr`
 * first-touch cookie's key set — `src/lib/analytics/first-touch.ts` (server
 * read) and `posthog-provider.tsx`'s `persistFirstTouchAttribution` (client
 * write) both import it, so the two ends of that cookie can never diverge
 * again the way the old hand-mirrored 5-key lists could.
 */

import {
	type AiReferrerSource,
	classifyAiReferrer,
} from "@/lib/analytics/ai-referrer";
import {
	type ClickIdKind,
	classifyChannel,
	isSelfReferral,
	type MarketingChannel,
} from "@/lib/analytics/channel";

/**
 * The fields carried on the `ld_attr` first-touch cookie AND onto server
 * events read from it. Widened from the original 5 keys (P0.3(d)) — every
 * consumer of this cookie (client write, server read, the future
 * `acquisition_touches` promotion) must use this list, never its own copy.
 * Each value is capped at 200 chars ({@link capField}) well under the 4 KB
 * cookie ceiling this list's consumers document.
 */
export const FIRST_TOUCH_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
	"referrer_host",
	"ai_referrer",
	"click_id_kind",
	"click_id",
	"landing_path",
	"channel",
] as const;

export type FirstTouchKey = (typeof FIRST_TOUCH_KEYS)[number];

/**
 * The click-id query params a landing URL can carry, in the priority order a
 * caller should scan them (the first one present wins — a URL should never
 * carry two, but if it somehow does, the earliest network in the list is
 * treated as authoritative). Mirrors `ClickIdKind` in `channel.ts` and
 * `acquisitionTouches.clickIdKind` in `src/lib/db/schema.ts`.
 */
export const CLICK_ID_PARAMS: readonly ClickIdKind[] = [
	"gclid",
	"gbraid",
	"wbraid",
	"fbclid",
	"msclkid",
	"ttclid",
	"li_fat_id",
	"twclid",
];

export type AcquisitionSubjectKind = "anon" | "user" | "email";
export type AcquisitionTouchKind = "first" | "last";

/**
 * The full shape of one `acquisition_touches` row, as a plain type — kept
 * independent of the Drizzle table definition so this pure module never has
 * to import `src/lib/db/schema.ts`. `subjectKind`/`subjectId`/`touchKind`/
 * `landingPageFamily`/`occurredAt` are filled in by the write path (the
 * caller knows the subject; `landingPageFamily` is derived via
 * `classifyPageFamily` at write time, per the schema docstring) — everything
 * else comes from {@link normalizeTouch}.
 */
export type AcquisitionTouchPayload = {
	subjectKind: AcquisitionSubjectKind;
	subjectId: string;
	touchKind: AcquisitionTouchKind;
	channel: MarketingChannel;
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	utmTerm: string | null;
	utmContent: string | null;
	clickIdKind: ClickIdKind | null;
	clickId: string | null;
	referrerHost: string | null;
	aiReferrer: AiReferrerSource | null;
	landingPath: string | null;
	landingPageFamily: string | null;
	occurredAt: Date;
};

/** Raw, untrusted input to {@link normalizeTouch} — one URL's worth of signal. */
export type RawTouchInput = {
	utmSource?: string | null;
	utmMedium?: string | null;
	utmCampaign?: string | null;
	utmTerm?: string | null;
	utmContent?: string | null;
	clickIdKind?: string | null;
	clickId?: string | null;
	/** Raw referrer host — self-referral suppression happens inside this fn. */
	referrerHost?: string | null;
	hasReferralCode?: boolean;
	/** Full pathname + query string; the query string is stripped here. */
	landingPath?: string | null;
};

/**
 * The subset of {@link AcquisitionTouchPayload} derivable from a URL alone,
 * with no knowledge of the converting subject yet.
 */
export type NormalizedTouch = {
	channel: MarketingChannel;
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	utmTerm: string | null;
	utmContent: string | null;
	clickIdKind: ClickIdKind | null;
	clickId: string | null;
	referrerHost: string | null;
	aiReferrer: AiReferrerSource | null;
	/** Pathname only — query string stripped (PII + cardinality). */
	landingPath: string | null;
};

/**
 * Cap a value at `max` chars, trimmed. Returns `null` for empty/oversized
 * input rather than truncating — an oversized value is more likely a bug
 * (accidentally serialized JSON in a UTM param) than a legitimate long
 * campaign name, and silently truncating would hide that.
 */
function capField(v: string | null | undefined, max = 200): string | null {
	if (!v) return null;
	const trimmed = v.trim();
	return trimmed.length > 0 && trimmed.length <= max ? trimmed : null;
}

function isKnownClickIdKind(v: string | null | undefined): v is ClickIdKind {
	return !!v && (CLICK_ID_PARAMS as readonly string[]).includes(v);
}

/** Strip everything from the first `?` or `#` onward — pathname only. */
function stripQuery(path: string): string {
	const cut = path.search(/[?#]/);
	return cut === -1 ? path : path.slice(0, cut);
}

/**
 * Normalize one URL's worth of raw attribution signal into the fields
 * `acquisition_touches` stores: caps every string field, suppresses a
 * self-referral host (never emitted as `referrerHost`/`aiReferrer`, and never
 * fed to the channel classifier), strips the query string from `landingPath`,
 * and classifies the channel via {@link classifyChannel}. Never throws.
 */
export function normalizeTouch(input: RawTouchInput): NormalizedTouch {
	const rawHost = capField(input.referrerHost);
	const referrerHost = rawHost && !isSelfReferral(rawHost) ? rawHost : null;
	const clickIdKind = isKnownClickIdKind(input.clickIdKind)
		? input.clickIdKind
		: null;
	const channel = classifyChannel({
		utmSource: input.utmSource,
		utmMedium: input.utmMedium,
		utmCampaign: input.utmCampaign,
		clickIdKind,
		referrerHost,
		hasReferralCode: input.hasReferralCode,
	});

	return {
		channel,
		utmSource: capField(input.utmSource),
		utmMedium: capField(input.utmMedium),
		utmCampaign: capField(input.utmCampaign),
		utmTerm: capField(input.utmTerm),
		utmContent: capField(input.utmContent),
		clickIdKind,
		clickId: capField(input.clickId),
		referrerHost,
		aiReferrer: referrerHost ? classifyAiReferrer(referrerHost) : null,
		landingPath: input.landingPath
			? capField(stripQuery(input.landingPath))
			: null,
	};
}
